// =============================================================================
// princess-castle — b05 "Malika" card preview layout.
//
// A SUPER MAXIMALIST princess fairy-tale castle birthday card. Cotton-candy
// pink gradient sky, fairy-tale castle silhouette with three turrets and
// pennants, scattered gold-foil stars (varying sizes, some with trail lines),
// a magic-wand crossing diagonally with a sparkle trail, butterflies flying
// across, a jeweled tiara above the names, photo in heart-shaped frame with
// gold rim, and a pink ribbon "5 YOSH" banner at the bottom. Corners hold
// fairy-dust swirls. Names render in giant Pinyon Script italic gold.
//
// Palette is hard-coded in the catalog: bg #FFE9F0, ink #3A1F2E, accent
// #D8B14C. Everything is inline SVG so card thumbnails render crisply at any
// DPR with zero network traffic.
// =============================================================================
import { Photo, type LayoutCtx } from "./_shared";

export function PrincessCastleLayout({ names, date, venue, accent, ink, bg, font, photo }: LayoutCtx) {
  // Pre-baked star positions: a curated scatter that reads as a sparkling
  // princess sky without random-jitter noise at thumbnail size.
  // [x%, y%, size, kind] — 0 = 5-pointed star, 1 = 4-point sparkle, 2 = pinpoint w/ trail
  const stars: Array<[number, number, number, 0 | 1 | 2]> = [
    [6, 8, 2.4, 0], [12, 18, 1.6, 1], [18, 5, 2.0, 2], [24, 14, 1.8, 1],
    [33, 7, 2.6, 0], [40, 18, 1.4, 1], [46, 4, 2.2, 0], [55, 12, 1.6, 2],
    [64, 6, 2.4, 0], [72, 14, 1.8, 1], [80, 8, 2.0, 0], [88, 16, 1.6, 1],
    [94, 6, 2.2, 2], [4, 28, 1.4, 1], [16, 32, 2.0, 0], [30, 26, 1.6, 1],
    [44, 30, 2.2, 0], [58, 26, 1.4, 1], [70, 32, 2.0, 2], [86, 28, 1.6, 1],
    [10, 48, 1.8, 0], [28, 44, 1.4, 1], [44, 50, 1.8, 1], [62, 46, 2.0, 0],
    [82, 50, 1.6, 1],
  ];

  // Derive a soft pink mid-tone from accent + bg for gradients
  const pinkMid = "#FFD0DD";
  const pinkDeep = "#F7B6C7";
  const ribbonPink = "#E689A0";
  const goldDeep = "#B7942A";

  return (
    <div style={{ position: "absolute", inset: 0, background: bg, color: ink, overflow: "hidden", fontFamily: font }}>
      {/* Soft pink gradient sky — bottom sweetens to deeper rose */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: `linear-gradient(180deg, #FFF4F8 0%, ${bg} 35%, ${pinkMid} 75%, ${pinkDeep} 100%)`,
        }}
      />

      {/* Faint gold radial glow centred behind the names */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: `radial-gradient(ellipse at 50% 60%, ${accent}22 0%, transparent 55%)`,
        }}
      />

      {/* ─────────────── Stars + sparkle trails (back layer) ─────────────── */}
      <svg viewBox="0 0 100 140" preserveAspectRatio="none" style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}>
        {stars.map(([x, y, s, kind], i) => {
          if (kind === 0) {
            // 5-pointed gold-foil star
            const pts: string[] = [];
            for (let k = 0; k < 10; k++) {
              const a = (Math.PI / 5) * k - Math.PI / 2;
              const r = k % 2 === 0 ? s : s * 0.42;
              pts.push(`${(x + Math.cos(a) * r).toFixed(2)},${(y + Math.sin(a) * r).toFixed(2)}`);
            }
            return <polygon key={i} points={pts.join(" ")} fill={accent} opacity={0.92} />;
          }
          if (kind === 1) {
            // 4-point sparkle (cross)
            return (
              <g key={i} opacity={0.9}>
                <path d={`M${x} ${y - s} L${x + s * 0.22} ${y - s * 0.22} L${x + s} ${y} L${x + s * 0.22} ${y + s * 0.22} L${x} ${y + s} L${x - s * 0.22} ${y + s * 0.22} L${x - s} ${y} L${x - s * 0.22} ${y - s * 0.22} Z`} fill={accent} />
              </g>
            );
          }
          // pinpoint with a hairline trail
          return (
            <g key={i} opacity={0.85}>
              <circle cx={x} cy={y} r={s * 0.42} fill={accent} />
              <line x1={x - s * 1.6} y1={y + s * 0.4} x2={x} y2={y} stroke={accent} strokeWidth={0.18} opacity={0.65} />
            </g>
          );
        })}

        {/* ─────────────── Magic wand crossing diagonally ─────────────── */}
        <g transform="translate(0 0)">
          {/* Wand stick */}
          <line x1="14" y1="118" x2="40" y2="64" stroke={goldDeep} strokeWidth="0.9" strokeLinecap="round" />
          <line x1="14" y1="118" x2="40" y2="64" stroke={accent} strokeWidth="0.45" strokeLinecap="round" />
          {/* Wand handle wrap */}
          {Array.from({ length: 5 }, (_, k) => (
            <line key={k} x1={14 + k * 1.4} y1={118 - k * 2.9} x2={16 + k * 1.4} y2={114 - k * 2.9} stroke={ribbonPink} strokeWidth="0.35" />
          ))}
          {/* Star at the tip */}
          {(() => {
            const cx = 40, cy = 64, r = 4.2;
            const pts: string[] = [];
            for (let k = 0; k < 10; k++) {
              const a = (Math.PI / 5) * k - Math.PI / 2;
              const rr = k % 2 === 0 ? r : r * 0.42;
              pts.push(`${(cx + Math.cos(a) * rr).toFixed(2)},${(cy + Math.sin(a) * rr).toFixed(2)}`);
            }
            return (
              <g>
                <polygon points={pts.join(" ")} fill={accent} stroke={goldDeep} strokeWidth="0.3" />
                <circle cx={cx} cy={cy} r="0.9" fill="#FFF8D6" />
              </g>
            );
          })()}
          {/* Sparkle trail dots along the wand */}
          {[0.15, 0.32, 0.5, 0.68, 0.85].map((tt, k) => {
            const tx = 14 + (40 - 14) * tt + (k % 2 === 0 ? 1.2 : -1.2);
            const ty = 118 + (64 - 118) * tt + (k % 2 === 0 ? -1.4 : 1.4);
            return <circle key={k} cx={tx} cy={ty} r={0.45 + k * 0.08} fill={accent} opacity={0.8} />;
          })}
        </g>

        {/* ─────────────── Butterflies (3) ─────────────── */}
        {[
          [78, 28, 1.0, accent],
          [22, 40, 0.85, ribbonPink],
          [88, 60, 1.1, accent],
          [62, 38, 0.75, ribbonPink],
          [10, 70, 0.9, accent],
        ].map(([cx, cy, scl, col], k) => (
          <g key={`bf${k}`} transform={`translate(${cx} ${cy}) scale(${scl})`} opacity={0.9}>
            {/* Left wing */}
            <ellipse cx="-1.6" cy="-0.5" rx="2.2" ry="1.4" fill={col as string} opacity={0.9} />
            <ellipse cx="-1.4" cy="0.8" rx="1.6" ry="1.0" fill={col as string} opacity={0.75} />
            {/* Right wing */}
            <ellipse cx="1.6" cy="-0.5" rx="2.2" ry="1.4" fill={col as string} opacity={0.9} />
            <ellipse cx="1.4" cy="0.8" rx="1.6" ry="1.0" fill={col as string} opacity={0.75} />
            {/* Body */}
            <ellipse cx="0" cy="0" rx="0.3" ry="1.4" fill={ink} />
            {/* Antennae */}
            <path d="M0 -1.1 Q -0.4 -2 -0.9 -2.2" stroke={ink} strokeWidth="0.18" fill="none" />
            <path d="M0 -1.1 Q 0.4 -2 0.9 -2.2" stroke={ink} strokeWidth="0.18" fill="none" />
            {/* Wing dots */}
            <circle cx="-1.6" cy="-0.5" r="0.3" fill="#FFF8D6" />
            <circle cx="1.6" cy="-0.5" r="0.3" fill="#FFF8D6" />
          </g>
        ))}

        {/* ─────────────── FAIRY-TALE CASTLE silhouette at bottom ─────────────── */}
        <g>
          {/* Castle base wall */}
          <rect x="18" y="108" width="64" height="22" fill={ink} opacity="0.92" />
          {/* Crenellations on main wall */}
          {Array.from({ length: 12 }, (_, k) => (
            <rect key={k} x={18 + k * 5.4} y={106} width="3.2" height="3" fill={ink} opacity="0.92" />
          ))}
          {/* Gate */}
          <path d="M44 130 L44 120 Q50 112 56 120 L56 130 Z" fill={pinkDeep} opacity="0.9" />
          <path d="M44 130 L44 120 Q50 112 56 120 L56 130 Z" fill="none" stroke={accent} strokeWidth="0.4" />
          <line x1="50" y1="120" x2="50" y2="130" stroke={accent} strokeWidth="0.3" opacity="0.8" />
          {/* Small windows along the wall */}
          {[24, 32, 68, 76].map((wx, k) => (
            <g key={k}>
              <rect x={wx} y="116" width="2.6" height="4" fill={accent} opacity="0.85" />
              <rect x={wx} y="116" width="2.6" height="4" fill="none" stroke={ink} strokeWidth="0.2" />
            </g>
          ))}

          {/* LEFT TURRET */}
          <rect x="14" y="92" width="10" height="18" fill={ink} opacity="0.92" />
          <rect x="14" y="89" width="2.4" height="3" fill={ink} />
          <rect x="17.4" y="89" width="2.4" height="3" fill={ink} />
          <rect x="20.8" y="89" width="3.2" height="3" fill={ink} />
          {/* Conical roof */}
          <path d="M12 92 L19 76 L26 92 Z" fill={ribbonPink} stroke={accent} strokeWidth="0.3" />
          {/* Roof bands */}
          <line x1="13.5" y1="88" x2="24.5" y2="88" stroke={accent} strokeWidth="0.25" opacity="0.7" />
          <line x1="14.5" y1="84" x2="23.5" y2="84" stroke={accent} strokeWidth="0.25" opacity="0.7" />
          {/* Pennant */}
          <line x1="19" y1="76" x2="19" y2="68" stroke={accent} strokeWidth="0.4" />
          <path d="M19 68 L25 70 L19 72 Z" fill={accent} />
          {/* Window */}
          <rect x="17.5" y="98" width="3" height="5" rx="1.5" fill={accent} opacity="0.85" />

          {/* CENTRE TURRET (taller) */}
          <rect x="42" y="84" width="16" height="24" fill={ink} opacity="0.95" />
          {/* Crenellation top */}
          {[0, 1, 2, 3, 4].map((k) => (
            <rect key={k} x={42 + k * 3.2} y="81" width="2.4" height="3" fill={ink} />
          ))}
          {/* Tall conical roof */}
          <path d="M40 84 L50 60 L60 84 Z" fill={ribbonPink} stroke={accent} strokeWidth="0.35" />
          {/* Roof scallops */}
          <path d="M42 84 Q44 81 46 84 Q48 81 50 84 Q52 81 54 84 Q56 81 58 84" fill="none" stroke={accent} strokeWidth="0.3" />
          <line x1="41.5" y1="78" x2="58.5" y2="78" stroke={accent} strokeWidth="0.3" opacity="0.8" />
          <line x1="43" y1="72" x2="57" y2="72" stroke={accent} strokeWidth="0.3" opacity="0.7" />
          {/* Centre flag — bigger pennant */}
          <line x1="50" y1="60" x2="50" y2="50" stroke={accent} strokeWidth="0.55" />
          <path d="M50 50 L60 53 L50 56 Z" fill={accent} />
          <circle cx="50" cy="50" r="0.7" fill={accent} />
          {/* Centre window — gothic arch */}
          <path d="M46 96 L46 92 Q50 88 54 92 L54 96 Z" fill={accent} opacity="0.9" />
          <path d="M46 96 L46 92 Q50 88 54 92 L54 96 Z" fill="none" stroke={ink} strokeWidth="0.25" />
          <line x1="50" y1="89" x2="50" y2="96" stroke={ink} strokeWidth="0.18" />

          {/* RIGHT TURRET */}
          <rect x="76" y="94" width="10" height="16" fill={ink} opacity="0.92" />
          <rect x="76" y="91" width="2.4" height="3" fill={ink} />
          <rect x="79.4" y="91" width="2.4" height="3" fill={ink} />
          <rect x="82.8" y="91" width="3.2" height="3" fill={ink} />
          <path d="M74 94 L81 78 L88 94 Z" fill={ribbonPink} stroke={accent} strokeWidth="0.3" />
          <line x1="75.5" y1="90" x2="86.5" y2="90" stroke={accent} strokeWidth="0.25" opacity="0.7" />
          <line x1="76.5" y1="86" x2="85.5" y2="86" stroke={accent} strokeWidth="0.25" opacity="0.7" />
          <line x1="81" y1="78" x2="81" y2="70" stroke={accent} strokeWidth="0.4" />
          <path d="M81 70 L87 72 L81 74 Z" fill={accent} />
          <rect x="79.5" y="100" width="3" height="5" rx="1.5" fill={accent} opacity="0.85" />
        </g>

        {/* Tiara above the names — at ~y=70 area */}
        <g transform="translate(50 53)">
          {/* Headband arc */}
          <path d="M -14 4 Q 0 -2 14 4" fill="none" stroke={accent} strokeWidth="0.6" />
          <path d="M -14 4 Q 0 -2 14 4" fill="none" stroke={goldDeep} strokeWidth="0.3" />
          {/* Three points */}
          <path d="M -10 1 L -10 -4 L -7 1 Z" fill={accent} stroke={goldDeep} strokeWidth="0.2" />
          <path d="M -3 -1 L 0 -7 L 3 -1 Z" fill={accent} stroke={goldDeep} strokeWidth="0.2" />
          <path d="M 7 1 L 10 -4 L 10 1 Z" fill={accent} stroke={goldDeep} strokeWidth="0.2" />
          {/* Centre jewel */}
          <circle cx="0" cy="-2" r="1.4" fill={ribbonPink} stroke={accent} strokeWidth="0.25" />
          <circle cx="0" cy="-2.4" r="0.4" fill="#FFF8D6" />
          {/* Side jewels */}
          <circle cx="-10" cy="-1.4" r="0.7" fill={ribbonPink} stroke={accent} strokeWidth="0.18" />
          <circle cx="10" cy="-1.4" r="0.7" fill={ribbonPink} stroke={accent} strokeWidth="0.18" />
          {/* Pearl beads along headband */}
          {[-12, -7, -4, 4, 7, 12].map((px, k) => (
            <circle key={k} cx={px} cy={3} r={0.35} fill="#FFF8D6" stroke={accent} strokeWidth="0.12" />
          ))}
        </g>

        {/* 4-corner fairy-dust swirls */}
        {[
          [6, 6, 1],
          [94, 6, -1],
          [6, 134, 1],
          [94, 134, -1],
        ].map(([cx, cy, dir], k) => (
          <g key={`sw${k}`} transform={`translate(${cx} ${cy}) scale(${dir})`} opacity={0.85}>
            <path d="M 0 0 Q 4 -2 6 -5 Q 7 -7 4 -8" fill="none" stroke={accent} strokeWidth="0.35" />
            <circle cx="0" cy="0" r="0.5" fill={accent} />
            <circle cx="3" cy="-3" r="0.35" fill={accent} />
            <circle cx="6" cy="-5" r="0.4" fill={accent} />
            <circle cx="4" cy="-8" r="0.3" fill={accent} />
          </g>
        ))}
      </svg>

      {/* ─────────────── Photo in heart-shaped frame ─────────────── */}
      <div
        style={{
          position: "absolute",
          left: "50%",
          top: "26%",
          transform: "translate(-50%, -50%)",
          width: "32%",
          aspectRatio: "1 / 0.92",
        }}
      >
        <svg viewBox="0 0 100 92" preserveAspectRatio="none" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", overflow: "visible" }}>
          <defs>
            <clipPath id="heartClip-b05">
              <path d="M50 88 C 18 64 6 40 18 22 C 28 8 42 12 50 24 C 58 12 72 8 82 22 C 94 40 82 64 50 88 Z" />
            </clipPath>
          </defs>
          {/* Outer gold rim */}
          <path d="M50 88 C 18 64 6 40 18 22 C 28 8 42 12 50 24 C 58 12 72 8 82 22 C 94 40 82 64 50 88 Z" fill={accent} />
          {/* Inner photo area (clipped) */}
          <g clipPath="url(#heartClip-b05)">
            <rect x="3" y="3" width="94" height="86" fill={`${ink}11`} />
          </g>
          {/* Inner gold hairline */}
          <path d="M50 84 C 22 62 12 40 22 26 C 30 14 42 18 50 28 C 58 18 70 14 78 26 C 88 40 78 62 50 84 Z" fill="none" stroke={goldDeep} strokeWidth="0.6" />
          {/* Gem accents at heart top notch */}
          <circle cx="50" cy="22" r="1.6" fill={ribbonPink} stroke={accent} strokeWidth="0.4" />
          <circle cx="50" cy="22" r="0.5" fill="#FFF8D6" />
        </svg>
        {/* Actual photo positioned inside via clip */}
        <div
          style={{
            position: "absolute",
            inset: "8% 10%",
            clipPath:
              "path('M50 80 C 22 60 14 40 22 26 C 30 16 40 18 50 28 C 60 18 70 16 78 26 C 86 40 78 60 50 80 Z')",
            WebkitClipPath:
              "path('M50 80 C 22 60 14 40 22 26 C 30 16 40 18 50 28 C 60 18 70 16 78 26 C 86 40 78 60 50 80 Z')",
            // viewport for clipPath uses pixels — provide a backup background
            background: `radial-gradient(circle at 40% 30%, ${pinkMid}, ${pinkDeep})`,
            overflow: "hidden",
          }}
        >
          <Photo
            src={photo}
            style={{ width: "100%", height: "100%" }}
            fallback={
              <div
                style={{
                  width: "100%",
                  height: "100%",
                  display: "grid",
                  placeItems: "center",
                  fontFamily: font,
                  fontStyle: "italic",
                  fontSize: 14,
                  color: accent,
                }}
              >
                ♡
              </div>
            }
          />
        </div>
      </div>

      {/* ─────────────── Names — giant Pinyon Script italic gold ─────────────── */}
      <div
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          top: "52%",
          textAlign: "center",
          padding: "0 6%",
        }}
      >
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6.5,
            fontWeight: 700,
            letterSpacing: "0.42em",
            color: ink,
            opacity: 0.85,
            marginBottom: 4,
          }}
        >
          ✦ MALIKAMIZ ✦
        </div>
        <div
          style={{
            fontFamily: font,
            fontStyle: "italic",
            fontSize: 26,
            fontWeight: 400,
            color: accent,
            lineHeight: 1.0,
            letterSpacing: "0.005em",
            textShadow: `0 1px 0 ${goldDeep}66, 0 0 8px ${accent}44`,
          }}
        >
          {names.split(" ").slice(0, 2).join(" ")}
        </div>
        {/* Hairline gold rule with centre diamond */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, marginTop: 5 }}>
          <span style={{ flex: 1, maxWidth: "30%", height: 1, background: `linear-gradient(90deg, transparent, ${accent}, transparent)` }} />
          <span style={{ color: accent, fontSize: 8, lineHeight: 1 }}>♛</span>
          <span style={{ flex: 1, maxWidth: "30%", height: 1, background: `linear-gradient(90deg, transparent, ${accent}, transparent)` }} />
        </div>
      </div>

      {/* ─────────────── Pink ribbon banner "5 YOSH" — date · venue ─────────────── */}
      <div
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: "5%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 4,
        }}
      >
        <div style={{ position: "relative", width: "62%", height: 18 }}>
          <svg viewBox="0 0 200 32" preserveAspectRatio="none" style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}>
            {/* Ribbon tails */}
            <path d="M0 4 L18 16 L0 28 L8 16 Z" fill={goldDeep} />
            <path d="M200 4 L182 16 L200 28 L192 16 Z" fill={goldDeep} />
            {/* Main banner */}
            <path d="M14 4 L186 4 L194 16 L186 28 L14 28 L6 16 Z" fill={ribbonPink} stroke={accent} strokeWidth="0.6" />
            {/* Highlight stripe */}
            <path d="M14 8 L186 8 L188 16 L186 24 L14 24 L12 16 Z" fill="none" stroke={accent} strokeWidth="0.4" opacity="0.7" />
          </svg>
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "grid",
              placeItems: "center",
              fontFamily: "var(--font-ui)",
              fontSize: 8.5,
              fontWeight: 800,
              letterSpacing: "0.32em",
              color: bg,
              textShadow: `0 1px 0 ${goldDeep}`,
            }}
          >
            ✦ 5 YOSH ✦
          </div>
        </div>
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6.5,
            fontWeight: 700,
            letterSpacing: "0.28em",
            color: ink,
            marginTop: 3,
          }}
        >
          {date} · {venue.toUpperCase()}
        </div>
      </div>

      {/* Frame border — gold hairline */}
      <div style={{ position: "absolute", inset: 4, border: `1px solid ${accent}`, opacity: 0.55, pointerEvents: "none" }} />
      <div style={{ position: "absolute", inset: 6, border: `0.5px solid ${accent}`, opacity: 0.3, pointerEvents: "none" }} />
    </div>
  );
}
